import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CurrentsAPI,
  formatCostMillicents,
  formatUsdMicros,
  type OceanAgent,
  type OceanMissionSummary,
  type OceanObsAggregate,
} from "@vida/currents-core";
import { createCurrentsApiConfig } from "../services/currentsSession";
import { resolveUsers } from "../api";
import { MissionOriginChip } from "../components/agent-test/MissionOriginBanner";
import {
  extractSubFromUserId,
  extractUserQuestion,
  parseHarborAssessmentLink,
  parseMissionOriginFromSummary,
  resolveOriginUserSub,
} from "../utils/missionTrace";

type GroupBy = "DAY" | "AGENT" | "TOOL" | "MODEL" | "APP";

const KNOWN_APP_IDS = ["currents", "wave", "surf", "tide", "pulse", "drift", "reef", "harbor"];

const HARBOR_ASSESSMENT_BASE = "https://harbor.oceany.ai/projects";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function missionChargedUsd(m: OceanMissionSummary): number {
  if (m.totalChargedUsdMicros != null && m.totalChargedUsdMicros > 0) {
    return m.totalChargedUsdMicros;
  }
  return (m.totalCostMillicents ?? 0) * 10;
}

function missionProviderUsd(m: OceanMissionSummary): number | null {
  if (m.totalProviderUsdMicros != null && m.totalProviderUsdMicros > 0) {
    return m.totalProviderUsdMicros;
  }
  return null;
}

function BarChart({ rows, valueKey }: { rows: OceanObsAggregate[]; valueKey: keyof OceanObsAggregate }) {
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  return (
    <div className="insights-bars" role="img" aria-label="Grafico a barre">
      {rows.map((row) => {
        const val = Number(row[valueKey]) || 0;
        const pct = Math.max(4, Math.round((val / max) * 100));
        return (
          <div key={row.bucket} className="insights-bar-row">
            <div className="insights-bar-label" title={row.bucket}>
              {row.bucket.length > 28 ? `${row.bucket.slice(0, 25)}…` : row.bucket}
            </div>
            <div className="insights-bar-track">
              <div className="insights-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="insights-bar-value">{val.toLocaleString("it-IT")}</div>
          </div>
        );
      })}
    </div>
  );
}

export function InsightsPage() {
  const [from, setFrom] = useState(() => isoDaysAgo(7));
  const [to, setTo] = useState(() => new Date().toISOString());
  const [agentId, setAgentId] = useState("");
  const [appId, setAppId] = useState("");
  const [status, setStatus] = useState("");
  const [triggerType, setTriggerType] = useState("");
  const [userSub, setUserSub] = useState("");
  const [groupBy, setGroupBy] = useState<GroupBy>("DAY");
  const [agents, setAgents] = useState<OceanAgent[]>([]);
  const [missions, setMissions] = useState<OceanMissionSummary[]>([]);
  const [aggregates, setAggregates] = useState<OceanObsAggregate[]>([]);
  const [userLabels, setUserLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const cfg = createCurrentsApiConfig();
        const agentList = await CurrentsAPI.listAgents(cfg);
        setAgents(agentList);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = createCurrentsApiConfig();
        const [missionRows, aggRows] = await Promise.all([
          CurrentsAPI.listMissions(cfg, {
            from,
            to,
            agentId: agentId || undefined,
            appId: appId || undefined,
            status: status || undefined,
            triggerType: triggerType || undefined,
            limit: 100,
          }),
          CurrentsAPI.getObsInsights(cfg, from, to, groupBy, undefined, appId || undefined),
        ]);
        setMissions(missionRows);
        setAggregates(aggRows);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [from, to, agentId, appId, status, triggerType, groupBy]);

  useEffect(() => {
    const subs = new Set<string>();
    for (const m of missions) {
      const origin = parseMissionOriginFromSummary(m);
      const sub = resolveOriginUserSub(origin);
      if (sub) subs.add(sub);
    }
    if (subs.size === 0) {
      setUserLabels({});
      return;
    }
    void (async () => {
      try {
        const users = await resolveUsers([...subs].slice(0, 20));
        const map: Record<string, string> = {};
        for (const u of users) {
          const label = u.displayName?.trim() || u.email?.trim() || u.sub;
          if (label) map[u.sub] = label;
        }
        setUserLabels(map);
      } catch {
        setUserLabels({});
      }
    })();
  }, [missions]);

  const filteredMissions = useMemo(() => {
    const needle = userSub.trim().toLowerCase();
    if (!needle) return missions;
    return missions.filter((m) => {
      const origin = parseMissionOriginFromSummary(m);
      const sub = resolveOriginUserSub(origin) || "";
      const label = userLabels[sub] || sub;
      return (
        sub.toLowerCase().includes(needle) ||
        label.toLowerCase().includes(needle) ||
        (m.userId && extractSubFromUserId(m.userId)?.toLowerCase().includes(needle))
      );
    });
  }, [missions, userSub, userLabels]);

  const appOptions = useMemo(() => {
    const ids = new Set(KNOWN_APP_IDS);
    for (const m of missions) {
      ids.add(m.appId?.trim() || "currents");
    }
    return [...ids].sort();
  }, [missions]);

  const kpis = useMemo(() => {
    const total = filteredMissions.length;
    const failed = filteredMissions.filter((m) => m.status === "FAILED").length;
    const chargedMicros = filteredMissions.reduce((s, m) => s + missionChargedUsd(m), 0);
    const providerMicros = filteredMissions.reduce((s, m) => s + (missionProviderUsd(m) ?? 0), 0);
    const inTok = filteredMissions.reduce((s, m) => s + (m.totalInputTokens ?? 0), 0);
    const outTok = filteredMissions.reduce((s, m) => s + (m.totalOutputTokens ?? 0), 0);
    const dur = filteredMissions.reduce((s, m) => s + (m.durationMs ?? 0), 0);
    const avgDur = total > 0 ? Math.round(dur / total) : 0;
    const unmatched = filteredMissions.filter((m) => m.pricingUnmatched).length;
    return { total, failed, chargedMicros, providerMicros, inTok, outTok, avgDur, unmatched };
  }, [filteredMissions]);

  const chartValueKey: keyof OceanObsAggregate =
    groupBy === "DAY" ? "missionCount" : "totalChargedUsdMicros";

  return (
    <div className="page insights-page">
      <header className="page-header">
        <div>
          <h1>Insights</h1>
          <p className="muted">
            Osservabilità cross-missione — costo cloud AWS vs addebitato (× markup), tempi e volumi.
          </p>
        </div>
      </header>

      <section className="card insights-filters">
        <div className="insights-filter-grid">
          <label>
            Da
            <input type="datetime-local" value={from.slice(0, 16)} onChange={(e) => setFrom(new Date(e.target.value).toISOString())} />
          </label>
          <label>
            A
            <input type="datetime-local" value={to.slice(0, 16)} onChange={(e) => setTo(new Date(e.target.value).toISOString())} />
          </label>
          <label>
            Agente
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">Tutti</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Applicazione
            <select value={appId} onChange={(e) => setAppId(e.target.value)}>
              <option value="">Tutte</option>
              {appOptions.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Tutti</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </label>
          <label>
            Tipo trigger
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)}>
              <option value="">Tutti</option>
              <option value="USER">Utente</option>
              <option value="APP">App</option>
              <option value="CRON">Cron</option>
              <option value="WEBHOOK">Webhook</option>
              <option value="API">API</option>
            </select>
          </label>
          <label>
            Utente (sub / email)
            <input
              type="search"
              value={userSub}
              onChange={(e) => setUserSub(e.target.value)}
              placeholder="Filtra per utente…"
            />
          </label>
          <label>
            Raggruppa
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
              <option value="DAY">Giorno</option>
              <option value="AGENT">Agente</option>
              <option value="TOOL">Tool</option>
              <option value="MODEL">Modello</option>
              <option value="APP">Applicazione</option>
            </select>
          </label>
        </div>
      </section>

      {error && <p className="error-banner">{error}</p>}
      {loading && <p className="muted">Caricamento…</p>}

      {!loading && (
        <>
          <section className="insights-kpi-grid">
            <article className="card insights-kpi">
              <div className="muted">Missioni</div>
              <strong>{kpis.total}</strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">% fallite</div>
              <strong>{kpis.total ? Math.round((kpis.failed / kpis.total) * 100) : 0}%</strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">Costo cloud</div>
              <strong>
                {kpis.providerMicros > 0 ? formatUsdMicros(kpis.providerMicros) : "—"}
              </strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">Addebitato</div>
              <strong>{formatUsdMicros(kpis.chargedMicros)}</strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">Stime non match</div>
              <strong>{kpis.unmatched}</strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">Token in → out</div>
              <strong>
                {kpis.inTok.toLocaleString("it-IT")} → {kpis.outTok.toLocaleString("it-IT")}
              </strong>
            </article>
            <article className="card insights-kpi">
              <div className="muted">Durata media</div>
              <strong>{formatMs(kpis.avgDur)}</strong>
            </article>
          </section>

          <section className="card insights-chart-card">
            <h2>Aggregati ({groupBy})</h2>
            {aggregates.length === 0 ? (
              <p className="muted">Nessun dato nel periodo selezionato.</p>
            ) : (
              <BarChart rows={aggregates} valueKey={chartValueKey} />
            )}
          </section>

          <section className="card">
            <h2>Missioni recenti</h2>
            {filteredMissions.length === 0 ? (
              <p className="muted">Nessuna missione con summary nel range.</p>
            ) : (
              <div className="insights-table-wrap">
                <table className="insights-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Origine</th>
                      <th>Agente</th>
                      <th>Status</th>
                      <th>Cloud</th>
                      <th>Addebitato</th>
                      <th>Ledger</th>
                      <th>Durata</th>
                      <th>Token</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMissions.map((m) => {
                      const provider = missionProviderUsd(m);
                      const charged = missionChargedUsd(m);
                      const origin = parseMissionOriginFromSummary(m);
                      const sub = resolveOriginUserSub(origin);
                      const userLabel = sub ? userLabels[sub] || sub : null;
                      const question = extractUserQuestion({
                        userQuestion: m.userQuestion,
                        goalPreview: m.goalPreview,
                      });
                      const harborLink =
                        m.status === "FAILED"
                          ? parseHarborAssessmentLink({
                              appId: m.appId,
                              contextJson: m.contextJson,
                            })
                          : null;
                      return (
                        <tr key={m.jobId}>
                          <td>
                            <code>{m.jobId.slice(0, 8)}…</code>
                            {m.pricingUnmatched && (
                              <span className="chip chip--warn" title="Rate modello non in tabella">
                                ⚠️
                              </span>
                            )}
                            {question && (
                              <div className="muted insights-goal-preview" title={question}>
                                {question.length > 80 ? `${question.slice(0, 77)}…` : question}
                              </div>
                            )}
                          </td>
                          <td>
                            <MissionOriginChip
                              appId={m.appId}
                              userLabel={userLabel}
                              userQuestion={question || null}
                            />
                          </td>
                          <td>{m.agentId}</td>
                          <td>
                            <span className={`chip ${m.status === "FAILED" ? "chip--danger" : ""}`}>
                              {m.status}
                            </span>
                          </td>
                          <td>{provider != null ? formatUsdMicros(provider) : "—"}</td>
                          <td>
                            {formatUsdMicros(charged)}
                            {m.markup != null && (
                              <span className="muted insights-markup"> ×{m.markup}</span>
                            )}
                          </td>
                          <td>
                            {m.ledgerCents != null
                              ? `${m.ledgerCents}¢`
                              : formatCostMillicents(m.totalCostMillicents ?? 0, 2)}
                          </td>
                          <td>{formatMs(m.durationMs ?? 0)}</td>
                          <td>
                            {(m.totalInputTokens ?? 0).toLocaleString("it-IT")} →{" "}
                            {(m.totalOutputTokens ?? 0).toLocaleString("it-IT")}
                          </td>
                          <td>
                            <Link
                              className="btn secondary"
                              to={`/agents/${m.agentId}/test?jobId=${m.jobId}&from=insights`}
                            >
                              Trace
                            </Link>
                            {harborLink && (
                              <a
                                className="btn secondary"
                                href={`${HARBOR_ASSESSMENT_BASE}/${harborLink.projectId}/assessments/${harborLink.assessmentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ marginLeft: "0.35rem" }}
                              >
                                Apri in Harbor ↗
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
